import { createReducer, on } from '@ngrx/store';
import { DeviceState as AppManagementDeviceState } from '@rocksoft/appmanagement-sdk';
import { AppleDevice, DeviceDto as LocationsDevice } from '@rocksoft/locations-sdk';
import { Device as MdmDevice } from '@rocksoft/mdm-sdk-v2';
import { Device } from '@rocksoft/shared';
import { DeviceApiActions, DeviceResolverActions, DeviceSignalRHubActions } from './device.actions';
import { adapter, initialState } from './device.state';

export const DEVICE_FEATURE_KEY = 'devices';

/**
 * Maps a LocationsDevice to Device, preserving existing data and normalizing overlapping fields
 */
function mapLocationsDevice(device: LocationsDevice, existing?: Device): Device {
  const udid = device.deviceId || existing?.udid || '';

  return {
    ...existing,
    udid,
    // Normalized fields
    name: device.deviceName ?? existing?.name ?? null,
    serialNumber: device.serialNumber ?? existing?.serialNumber ?? null,
    lastSeen: device.lastSeen ?? existing?.lastSeen ?? null,
    isLostModeEnabled: device.isMDMLostModeEnabled ?? existing?.isLostModeEnabled ?? null,
    // Locations-specific fields
    // Preserve existing values when an update payload omits a field, while
    // allowing explicit nulls to clear assignments (for example, unassigning
    // a device from a room).
    categoryId: device.categoryId !== undefined ? device.categoryId : (existing?.categoryId ?? null),
    locationsDeviceId: device.id !== undefined ? device.id : (existing?.locationsDeviceId ?? null),
    deviceType: device.deviceType !== undefined ? device.deviceType : existing?.deviceType,
    brandCategoryId: device.brandUniqueId !== undefined ? device.brandUniqueId : (existing?.brandCategoryId ?? null),
    locationCategoryId:
      device.locationUniqueId !== undefined ? device.locationUniqueId : (existing?.locationCategoryId ?? null),
    roomCategoryId: device.roomUniqueId !== undefined ? device.roomUniqueId : (existing?.roomCategoryId ?? null),
    frontDeskCategoryId:
      device.frontDeskUniqueId !== undefined ? device.frontDeskUniqueId : (existing?.frontDeskCategoryId ?? null),
    housekeepingCategoryId:
      device.housekeepingUniqueId !== undefined
        ? device.housekeepingUniqueId
        : (existing?.housekeepingCategoryId ?? null),
    barCategoryId: device.barUniqueId !== undefined ? device.barUniqueId : (existing?.barCategoryId ?? null),
    staffCategoryId: device.staffUniqueId !== undefined ? device.staffUniqueId : (existing?.staffCategoryId ?? null),
    kioskCategoryId: device.kioskUniqueId !== undefined ? device.kioskUniqueId : (existing?.kioskCategoryId ?? null),
    state: device.state !== undefined ? device.state : existing?.state,
    deviceNameOverride:
      device.deviceNameOverride !== undefined ? device.deviceNameOverride : (existing?.deviceNameOverride ?? null),
    apple: device.apple !== undefined ? device.apple : existing?.apple,
    hasLoadedLocationsDevice: true,
  };
}

/**
 * Maps an AppleDevice to Device, preserving existing data and normalizing overlapping fields
 */
function mapAppleDevice(appleDevice: AppleDevice, existing?: Device): Device {
  return {
    ...existing,
    udid: appleDevice.udid || existing?.udid || '',
    // Normalized fields (prefer AppleDevice values if they exist)
    name: appleDevice.deviceName ?? existing?.name ?? null,
    serialNumber: appleDevice.serialNumber ?? existing?.serialNumber ?? null,
    isLostModeEnabled: appleDevice.isMdmLostModeEnabled ?? existing?.isLostModeEnabled ?? null,
    // AppleDevice-specific fields
    osVersion: appleDevice.osVersion ?? null,
    buildVersion: appleDevice.buildVersion ?? null,
    model: appleDevice.model ?? null,
    productName: appleDevice.productName ?? null,
    osUpdates: appleDevice.osUpdates ?? null,
    managedApplications: appleDevice.managedApplications ?? null,
    hasLoadedAppleDevice: true,
  };
}

/**
 * Maps DeviceState array to Device entities, preserving existing data
 */
function mapApplicationStates(
  states: AppManagementDeviceState[],
  existingEntities: Record<string, Device | undefined>
): Device[] {
  return states.map((state) => {
    const existing = existingEntities[state.udid || ''];
    return {
      ...existing,
      udid: state.udid || existing?.udid || '',
      inUse: state.inUse,
      hasLoadedInUse: true,
    };
  });
}

/**
 * Maps MdmDevice array to Device entities, preserving existing data and normalizing overlapping fields
 */
function mapMdmDevices(devices: MdmDevice[], existingEntities: Record<string, Device | undefined>): Device[] {
  return devices.map((device) => {
    const existing = existingEntities[device.identifier || ''];
    // MDM Device may have different field names - access safely
    const displayName = device.displayName;
    const deviceType = device.type;
    const serialNumber = device.serialNumber;
    const lastSeen = device.lastSeen;

    return {
      ...existing,
      udid: device.identifier || existing?.udid || '',
      // Normalized fields (prefer MDM values if they exist)
      name: displayName ?? existing?.name ?? null,
      platform: deviceType ?? existing?.platform,
      serialNumber: serialNumber ?? existing?.serialNumber ?? null,
      lastSeen: lastSeen ?? existing?.lastSeen ?? null,
      hasLoadedMdmDevice: true,
    };
  });
}

export const deviceReducer = createReducer(
  initialState,

  // ============================================================================
  // RESOLVER ACTIONS
  // ============================================================================
  on(DeviceResolverActions.devicesRequested, (state) => ({
    ...state,
    error: undefined,
  })),
  on(DeviceResolverActions.devicesForStateRequested, (state) => ({
    ...state,
    error: undefined,
  })),
  on(DeviceResolverActions.appleDeviceRequested, (state) => ({
    ...state,
    error: undefined,
  })),
  on(DeviceResolverActions.deviceApplicationStatesRequested, (state) => ({
    ...state,
    error: undefined,
  })),
  on(DeviceResolverActions.mdmDevicesRequested, (state) => ({
    ...state,
    error: undefined,
  })),
  on(DeviceResolverActions.mdmDeviceRequested, (state) => ({
    ...state,
    error: undefined,
  })),

  // ============================================================================
  // PARTIAL DATA LOADS
  // ============================================================================

  // Basic device info (from device-store)
  on(DeviceApiActions.basicDeviceInfoLoaded, (state, { devices }) => {
    const mappedDevices = devices.map((device) => {
      const existing = state.entities[device.deviceId || ''];
      return mapLocationsDevice(device, existing);
    });

    return adapter.upsertMany(mappedDevices, {
      ...state,
      hasLoadedAnyLocationsDevice: true,
      error: undefined,
    });
  }),
  on(DeviceApiActions.basicDeviceInfoLoadError, (state, { error }) => ({
    ...state,
    error: error as Error,
  })),

  // Apple device info (from apple-device-store)
  on(DeviceApiActions.appleDeviceInfoLoaded, (state, { appleDevice }) => {
    const existing = state.entities[appleDevice.udid || ''];
    const mappedDevice = mapAppleDevice(appleDevice, existing);
    return adapter.upsertOne(mappedDevice, {
      ...state,
      hasLoadedAnyAppleDevice: true,
      error: undefined,
    });
  }),
  on(DeviceApiActions.appleDeviceInfoLoadError, (state, { error }) => ({
    ...state,
    error: error as Error,
  })),

  // Application state (from device-application-state)
  on(DeviceApiActions.applicationStateLoaded, (state, { states }) => {
    const devices = mapApplicationStates(states, state.entities);
    return adapter.upsertMany(devices, {
      ...state,
      hasLoadedAnyInUse: true,
      error: undefined,
    });
  }),
  on(DeviceApiActions.applicationStateLoadError, (state, { error }) => ({
    ...state,
    error: error as Error,
  })),

  // MDM device info (from mdm-devices)
  on(DeviceApiActions.mdmDeviceInfoLoaded, (state, { devices }) => {
    const mappedDevices = mapMdmDevices(devices, state.entities);
    return adapter.upsertMany(mappedDevices, {
      ...state,
      hasLoadedAnyMdmDevice: true,
      error: undefined,
    });
  }),
  on(DeviceApiActions.mdmDeviceInfoLoadError, (state, { error }) => ({
    ...state,
    error: error as Error,
  })),

  // ============================================================================
  // DEVICE STATE CHANGES
  // ============================================================================
  on(DeviceApiActions.categoryAssignmentSucceeded, (state, { device }) => {
    if (device === undefined) {
      return state;
    }

    const existing = state.entities[device.deviceId || ''];
    return adapter.upsertOne(mapLocationsDevice(device, existing), { ...state, error: undefined });
  }),
  on(DeviceApiActions.categoryAssignmentFailed, (state, { error }) => ({ ...state, error: error as Error })),
  on(DeviceApiActions.updateDeviceNameOverrideSucceeded, (state, { device }) => {
    const existing = state.entities[device.deviceId || ''];
    const mappedDevice = mapLocationsDevice(device, existing);
    return adapter.upsertOne(mappedDevice, {
      ...state,
      error: undefined,
    });
  }),

  // ============================================================================
  // LOST MODE OPERATIONS
  // ============================================================================
  on(DeviceApiActions.devicePutInLostMode, (state, { udid }) => {
    const existing = state.entities[udid];
    if (!existing) return state;

    return adapter.upsertOne(
      {
        ...existing,
        isLostModeEnabled: true,
      },
      state
    );
  }),
  on(DeviceApiActions.deviceTakenOutOfLostMode, (state, { udid }) => {
    const existing = state.entities[udid];
    if (!existing) return state;

    return adapter.upsertOne(
      {
        ...existing,
        isLostModeEnabled: false,
      },
      state
    );
  }),

  // ============================================================================
  // SIGNALR UPDATES
  // ============================================================================
  on(DeviceSignalRHubActions.deviceChanged, (state, { device }) => {
    const existing = state.entities[device.deviceId || ''];
    const mappedDevice = mapLocationsDevice(device, existing);
    return adapter.upsertOne(mappedDevice, state);
  }),
  on(DeviceSignalRHubActions.deviceRemoved, (state, { device }) => {
    return adapter.removeOne(device.deviceId || '', state);
  }),
  on(DeviceSignalRHubActions.mdmDeviceChanged, (state, { device }) => {
    const mappedDevice = mapMdmDevices([device], state.entities)[0];
    return adapter.upsertOne(mappedDevice, state);
  }),
  on(DeviceSignalRHubActions.mdmDeviceRemoved, (state, { device }) => {
    return adapter.removeOne(device.identifier || '', state);
  })
);

// Export adapter selectors
const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectDeviceIds = selectIds;
export const selectDevices = selectEntities;
export const selectAllDevices = selectAll;
export const selectTotalDevices = selectTotal;
